import { zodResolver } from '@hookform/resolvers/zod';
import {
  useListUnitTypes,
  useGetUnitType,
  useCreateUnitType,
  useUpdateUnitType,
  useDeleteUnitType,
  useCreateUnit,
  useUpdateUnit,
  useDeleteUnit,
  getListUnitTypesQueryKey,
  getGetUnitTypeQueryKey,
  type UnittypesUnitTypeSummaryResponse,
  type UnittypesUnitResponse,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, AlertCircle, Ruler, ChevronRight, Pencil, Trash2, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

const unitTypeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  color: z.union([
    z.literal(''),
    z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a hex value like #RRGGBB'),
  ]),
});

const unitSchema = z.object({
  symbol: z.string().min(1, 'Symbol is required').max(20, 'Symbol must be 20 characters or less'),
  label: z.string().min(1, 'Label is required').max(100, 'Label must be 100 characters or less'),
  color: z.union([
    z.literal(''),
    z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a hex value like #RRGGBB'),
  ]),
});

type UnitTypeFormData = z.infer<typeof unitTypeSchema>;
type UnitFormData = z.infer<typeof unitSchema>;

export function UnitTypesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useListUnitTypes();
  const createMutation = useCreateUnitType();
  const deleteMutation = useDeleteUnitType();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedTypeId, setExpandedTypeId] = useState<string | null>(null);
  const [typeToDelete, setTypeToDelete] = useState<UnittypesUnitTypeSummaryResponse | null>(null);
  const [typeToEdit, setTypeToEdit] = useState<UnittypesUnitTypeSummaryResponse | null>(null);

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: getListUnitTypesQueryKey() });
    if (expandedTypeId) {
      queryClient.invalidateQueries({ queryKey: getGetUnitTypeQueryKey(expandedTypeId) });
    }
  };

  const handleCreateType = async (formData: UnitTypeFormData) => {
    try {
      await createMutation.mutateAsync({
        data: { name: formData.name, color: formData.color || undefined },
      });
      invalidateQueries();
      setShowCreateModal(false);
    } catch {
      // Error is handled by the mutation state
    }
  };

  const handleDeleteType = async () => {
    if (!typeToDelete?.id) return;
    try {
      await deleteMutation.mutateAsync({ id: typeToDelete.id });
      invalidateQueries();
      setTypeToDelete(null);
      if (expandedTypeId === typeToDelete.id) {
        setExpandedTypeId(null);
      }
    } catch {
      // Error is handled by the mutation state
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedTypeId(expandedTypeId === id ? null : id);
  };

  const unitTypes = data?.unitTypes ?? [];

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              Unit Types
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage measurement units for sensors
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Unit Type
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch unit types</h3>
              <p className="mt-1 text-sm text-red-400/70">There was an error loading unit types.</p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Unit Types list */}
      {!isLoading && !isError && (
        <div>
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            Unit Types ({unitTypes.length})
          </h2>
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            {unitTypes.length > 0 ? (
              unitTypes.map((unitType, index) => (
                <UnitTypeCard
                  key={unitType.id}
                  unitType={unitType}
                  isFirst={index === 0}
                  isExpanded={expandedTypeId === unitType.id}
                  onToggle={() => toggleExpanded(unitType.id ?? '')}
                  onEdit={() => setTypeToEdit(unitType)}
                  onDelete={() => setTypeToDelete(unitType)}
                  onInvalidate={invalidateQueries}
                />
              ))
            ) : (
              <div className="p-8 text-center">
                <Ruler className="mx-auto h-8 w-8 text-gray-400 dark:text-zinc-600" />
                <p className="mt-3 text-sm text-gray-500 dark:text-zinc-500">No unit types</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 text-sm font-medium text-blue-500 transition-colors hover:text-blue-400"
                >
                  Create your first unit type
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Unit Type Modal */}
      {showCreateModal && (
        <UnitTypeModal
          title="Create Unit Type"
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateType}
          isLoading={createMutation.isPending}
          error={createMutation.error}
        />
      )}

      {/* Edit Unit Type Modal */}
      {typeToEdit && (
        <EditUnitTypeModal
          unitType={typeToEdit}
          onClose={() => setTypeToEdit(null)}
          onSuccess={() => {
            invalidateQueries();
            setTypeToEdit(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {typeToDelete && (
        <DeleteConfirmModal
          title="Delete Unit Type"
          message={
            <>
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-900 dark:text-white">{typeToDelete.name}</span>
              ? This will also delete all units within this type.
            </>
          }
          onClose={() => setTypeToDelete(null)}
          onConfirm={handleDeleteType}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

function UnitTypeCard({
  unitType,
  isFirst,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onInvalidate,
}: {
  unitType: UnittypesUnitTypeSummaryResponse;
  isFirst: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onInvalidate: () => void;
}) {
  return (
    <div className={!isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''}>
      {/* Header row */}
      <div className="flex items-center justify-between p-4">
        <button onClick={onToggle} className="flex flex-1 items-center gap-3 text-left">
          <ChevronRight
            className={`h-4 w-4 text-gray-400 transition-transform dark:text-zinc-600 ${
              isExpanded ? 'rotate-90' : ''
            }`}
          />
          <div>
            <div className="flex items-center gap-2 text-[15px] font-medium text-gray-900 dark:text-white">
              {unitType.color && (
                <span
                  className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10 dark:ring-white/10"
                  style={{ backgroundColor: unitType.color }}
                />
              )}
              <span>{unitType.name}</span>
            </div>
            <div className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
              {unitType.unitCount} {unitType.unitCount === 1 ? 'unit' : 'units'}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.03] dark:hover:text-zinc-300"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-red-500/10 hover:text-red-500 dark:text-zinc-500 dark:hover:text-red-400"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Expanded units section */}
      {isExpanded && unitType.id && (
        <UnitsSection unitTypeId={unitType.id} onInvalidate={onInvalidate} />
      )}
    </div>
  );
}

function UnitsSection({
  unitTypeId,
  onInvalidate,
}: {
  unitTypeId: string;
  onInvalidate: () => void;
}) {
  const { data, isLoading } = useGetUnitType(unitTypeId);
  const [showAddModal, setShowAddModal] = useState(false);
  const [unitToEdit, setUnitToEdit] = useState<UnittypesUnitResponse | null>(null);
  const [unitToDelete, setUnitToDelete] = useState<UnittypesUnitResponse | null>(null);
  const deleteUnitMutation = useDeleteUnit();

  const handleDeleteUnit = async () => {
    if (!unitToDelete?.id) return;
    try {
      await deleteUnitMutation.mutateAsync({ id: unitTypeId, unitId: unitToDelete.id });
      onInvalidate();
      setUnitToDelete(null);
    } catch {
      // Error handled by mutation state
    }
  };

  const units = data?.units ?? [];

  return (
    <div className="border-t border-gray-200 bg-gray-50/50 px-4 pb-4 pt-3 dark:border-white/[0.06] dark:bg-white/[0.02]">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-600 dark:text-zinc-400">Units</span>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1 text-sm text-blue-500 transition-colors hover:text-blue-400"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Unit
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      ) : units.length > 0 ? (
        <div className="space-y-2">
          {units.map((unit) => (
            <div
              key={unit.id}
              className="flex items-center justify-between rounded-lg bg-white p-3 dark:bg-[#18181b]"
            >
              <div className="flex items-center gap-3">
                <span className="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {unit.symbol}
                </span>
                {unit.color && (
                  <span
                    className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10 dark:ring-white/10"
                    style={{ backgroundColor: unit.color }}
                    title="Unit color override"
                  />
                )}
                <span className="text-sm text-gray-900 dark:text-white">{unit.label}</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setUnitToEdit(unit)}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-zinc-500 dark:hover:bg-white/[0.03] dark:hover:text-zinc-300"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setUnitToDelete(unit)}
                  className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-500 dark:text-zinc-500 dark:hover:text-red-400"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center dark:border-white/[0.1]">
          <p className="text-sm text-gray-500 dark:text-zinc-500">No units defined</p>
        </div>
      )}

      {/* Add Unit Modal */}
      {showAddModal && (
        <UnitModal
          title="Add Unit"
          unitTypeId={unitTypeId}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            onInvalidate();
            setShowAddModal(false);
          }}
        />
      )}

      {/* Edit Unit Modal */}
      {unitToEdit && (
        <UnitModal
          title="Edit Unit"
          unitTypeId={unitTypeId}
          unit={unitToEdit}
          onClose={() => setUnitToEdit(null)}
          onSuccess={() => {
            onInvalidate();
            setUnitToEdit(null);
          }}
        />
      )}

      {/* Delete Unit Confirmation */}
      {unitToDelete && (
        <DeleteConfirmModal
          title="Delete Unit"
          message={
            <>
              Are you sure you want to delete the unit{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {unitToDelete.symbol}
              </span>
              ?
            </>
          }
          onClose={() => setUnitToDelete(null)}
          onConfirm={handleDeleteUnit}
          isLoading={deleteUnitMutation.isPending}
        />
      )}
    </div>
  );
}

function UnitTypeModal({
  title,
  defaultValues,
  onClose,
  onSubmit,
  isLoading,
  error,
}: {
  title: string;
  defaultValues?: UnitTypeFormData;
  onClose: () => void;
  onSubmit: (data: UnitTypeFormData) => Promise<void>;
  isLoading: boolean;
  error: unknown;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UnitTypeFormData>({
    resolver: zodResolver(unitTypeSchema),
    defaultValues: defaultValues ?? { name: '', color: '' },
  });

  const colorValue = watch('color');
  const hasColor = (colorValue || '').trim().length > 0;
  const colorPickerRef = useRef<HTMLInputElement | null>(null);

  const openColorPicker = () => {
    const input = colorPickerRef.current;
    if (!input) return;
    const inputWithPicker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof inputWithPicker.showPicker === 'function') {
      inputWithPicker.showPicker();
      return;
    }
    inputWithPicker.click();
  };

  const clearColor = () => {
    setValue('color', '', { shouldValidate: true });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Name
            </label>
            <input
              {...register('name')}
              id="name"
              type="text"
              placeholder="e.g., Temperature"
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.06] dark:bg-[#111113] dark:text-white dark:placeholder-zinc-500"
            />
            {errors.name && <p className="mt-1.5 text-sm text-red-400">{errors.name.message}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
                Color (optional)
              </label>
              {hasColor && (
                <button
                  type="button"
                  onClick={clearColor}
                  className="text-sm font-medium text-blue-500 transition-colors hover:text-blue-400"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
              Used for sensor value display for this unit type
            </p>

            <input type="hidden" {...register('color')} />
            <input
              ref={colorPickerRef}
              type="color"
              value={colorValue || '#3b82f6'}
              onChange={(e) => setValue('color', e.target.value, { shouldValidate: true })}
              className="sr-only"
              aria-label="Unit type color picker"
            />

            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={openColorPicker}
                className={`h-10 w-12 rounded-lg border p-1 ${
                  hasColor
                    ? 'border-gray-300 dark:border-white/[0.06]'
                    : 'border-dashed border-gray-300 dark:border-white/[0.12]'
                }`}
                aria-label={hasColor ? 'Change unit type color' : 'Pick unit type color'}
                title={hasColor ? 'Change color' : 'Pick color'}
              >
                <div
                  className="h-full w-full rounded-md"
                  style={hasColor ? { backgroundColor: colorValue } : undefined}
                />
              </button>
              <button
                type="button"
                onClick={openColorPicker}
                className="text-sm font-medium text-blue-500 transition-colors hover:text-blue-400"
              >
                {hasColor ? 'Change' : 'Pick'}
              </button>
              <span className="font-mono text-sm text-gray-600 dark:text-zinc-400">
                {hasColor ? colorValue : 'Not set'}
              </span>
            </div>

            {errors.color && <p className="mt-1.5 text-sm text-red-400">{errors.color.message}</p>}
          </div>

          {error !== null && (
            <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">
              Failed to save unit type. Please try again.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              )}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUnitTypeModal({
  unitType,
  onClose,
  onSuccess,
}: {
  unitType: UnittypesUnitTypeSummaryResponse;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const updateMutation = useUpdateUnitType();

  const handleSubmit = async (data: UnitTypeFormData) => {
    if (!unitType.id) return;
    try {
      await updateMutation.mutateAsync({
        id: unitType.id,
        data: { name: data.name, color: data.color || undefined },
      });
      onSuccess();
    } catch {
      // Error handled by mutation state
    }
  };

  return (
    <UnitTypeModal
      title="Edit Unit Type"
      defaultValues={{ name: unitType.name ?? '', color: unitType.color ?? '' }}
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={updateMutation.isPending}
      error={updateMutation.error}
    />
  );
}

function UnitModal({
  title,
  unitTypeId,
  unit,
  onClose,
  onSuccess,
}: {
  title: string;
  unitTypeId: string;
  unit?: UnittypesUnitResponse;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const createMutation = useCreateUnit();
  const updateMutation = useUpdateUnit();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UnitFormData>({
    resolver: zodResolver(unitSchema),
    defaultValues: unit
      ? { symbol: unit.symbol ?? '', label: unit.label ?? '', color: unit.color ?? '' }
      : { symbol: '', label: '', color: '' },
  });

  const colorValue = watch('color');
  const hasColor = (colorValue || '').trim().length > 0;
  const colorPickerRef = useRef<HTMLInputElement | null>(null);

  const openColorPicker = () => {
    const input = colorPickerRef.current;
    if (!input) return;
    const inputWithPicker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof inputWithPicker.showPicker === 'function') {
      inputWithPicker.showPicker();
      return;
    }
    inputWithPicker.click();
  };

  const clearColor = () => {
    setValue('color', '', { shouldValidate: true });
  };

  const handleFormSubmit = async (data: UnitFormData) => {
    try {
      if (unit?.id) {
        await updateMutation.mutateAsync({
          id: unitTypeId,
          unitId: unit.id,
          data: { symbol: data.symbol, label: data.label, color: data.color || undefined },
        });
      } else {
        await createMutation.mutateAsync({
          id: unitTypeId,
          data: { symbol: data.symbol, label: data.label, color: data.color || undefined },
        });
      }
      onSuccess();
    } catch {
      // Error handled by mutation state
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-6 space-y-5">
          <div>
            <label
              htmlFor="symbol"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Symbol
            </label>
            <input
              {...register('symbol')}
              id="symbol"
              type="text"
              placeholder="e.g., °C"
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.06] dark:bg-[#111113] dark:text-white dark:placeholder-zinc-500"
            />
            {errors.symbol && (
              <p className="mt-1.5 text-sm text-red-400">{errors.symbol.message}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="label"
              className="block text-sm font-medium text-gray-700 dark:text-zinc-300"
            >
              Label
            </label>
            <input
              {...register('label')}
              id="label"
              type="text"
              placeholder="e.g., Celsius"
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.06] dark:bg-[#111113] dark:text-white dark:placeholder-zinc-500"
            />
            {errors.label && <p className="mt-1.5 text-sm text-red-400">{errors.label.message}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
                Color override (optional)
              </label>
              {hasColor && (
                <button
                  type="button"
                  onClick={clearColor}
                  className="text-sm font-medium text-blue-500 transition-colors hover:text-blue-400"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
              Overrides the unit type color for sensors using this unit
            </p>

            <input type="hidden" {...register('color')} />
            <input
              ref={colorPickerRef}
              type="color"
              value={colorValue || '#3b82f6'}
              onChange={(e) => setValue('color', e.target.value, { shouldValidate: true })}
              className="sr-only"
              aria-label="Unit color override picker"
            />

            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                onClick={openColorPicker}
                className={`h-10 w-12 rounded-lg border p-1 ${
                  hasColor
                    ? 'border-gray-300 dark:border-white/[0.06]'
                    : 'border-dashed border-gray-300 dark:border-white/[0.12]'
                }`}
                aria-label={hasColor ? 'Change unit color override' : 'Pick unit color override'}
                title={hasColor ? 'Change color' : 'Pick color'}
              >
                <div
                  className="h-full w-full rounded-md"
                  style={hasColor ? { backgroundColor: colorValue } : undefined}
                />
              </button>
              <button
                type="button"
                onClick={openColorPicker}
                className="text-sm font-medium text-blue-500 transition-colors hover:text-blue-400"
              >
                {hasColor ? 'Change' : 'Pick'}
              </button>
              <span className="font-mono text-sm text-gray-600 dark:text-zinc-400">
                {hasColor ? colorValue : 'Not set'}
              </span>
            </div>

            {errors.color && <p className="mt-1.5 text-sm text-red-400">{errors.color.message}</p>}
          </div>

          {error !== null && (
            <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">
              Failed to save unit. Please try again.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              )}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  title,
  message,
  onClose,
  onConfirm,
  isLoading,
}: {
  title: string;
  message: React.ReactNode;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-white/[0.06] dark:bg-[#18181b]">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            <p className="text-sm text-gray-500 dark:text-zinc-500">
              This action cannot be undone.
            </p>
          </div>
        </div>

        <p className="mt-4 text-sm text-gray-600 dark:text-zinc-400">{message}</p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-white/[0.06] dark:text-zinc-300 dark:hover:bg-white/[0.03]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
